import React from 'react';
import { LeadStatus } from '@/types/lead.types';

interface StatusBadgeProps {
  status: LeadStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<LeadStatus, { label: string; colors: string }> = {
  AVAILABLE: {
    label: 'Available',
    colors: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  OPEN: {
    label: 'Open',
    colors: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  CONTACTED: {
    label: 'Contacted',
    colors: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  },
  VISIT_SCHEDULED: {
    label: 'Visit Scheduled',
    colors: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  VISITED: {
    label: 'Visited',
    colors: 'bg-green-100 text-green-800 border-green-200',
  },
  NEGOTIATION: {
    label: 'Negotiation',
    colors: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  BOOKED: {
    label: 'Booked',
    colors: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  INVOICED: {
    label: 'Invoiced',
    colors: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  },
  PAID: {
    label: 'Paid',
    colors: 'bg-green-200 text-green-900 border-green-300',
  },
  DROPPED: {
    label: 'Dropped',
    colors: 'bg-red-100 text-red-800 border-red-200',
  },
  JUNK: {
    label: 'Junk',
    colors: 'bg-gray-200 text-gray-600 border-gray-300',
  },
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  className = '',
}) => {
  const config = statusConfig[status] || statusConfig.OPEN;

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full border
        ${config.colors}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {config.label}
    </span>
  );
};

export const getStatusLabel = (status: LeadStatus): string => {
  return statusConfig[status]?.label || status;
};

export const getAllStatuses = (): { value: LeadStatus; label: string }[] => {
  return Object.entries(statusConfig).map(([value, { label }]) => ({
    value: value as LeadStatus,
    label,
  }));
};

export default StatusBadge;
